import { Temporal } from '@js-temporal/polyfill'

function parsePlainDate(date) {
  if (date == null || date === '') return null
  try {
    if (typeof date === 'string') {
      return Temporal.PlainDate.from(date.split('T')[0])
    }
    if (date instanceof Date && !Number.isNaN(date.getTime())) {
      return Temporal.PlainDate.from({
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
      })
    }
    return null
  } catch {
    return null
  }
}

function formatDate(date, locale = 'fi') {
  const plain = parsePlainDate(date)
  if (!plain) return ''
  return plain.toLocaleString(locale, {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  })
}

function isDateOverdue(date) {
  const plain = parsePlainDate(date)
  if (!plain) return false
  return Temporal.PlainDate.compare(plain, Temporal.Now.plainDateISO()) < 0
}

function countDaysOverdue(date) {
  const plain = parsePlainDate(date)
  if (!plain) return null
  const today = Temporal.Now.plainDateISO()
  if (Temporal.PlainDate.compare(plain, today) >= 0) return 0
  return plain.until(today, { largestUnit: 'day' }).days
}

function normalizeIsoDate(date) {
  const plain = parsePlainDate(date)
  if (!plain) return ''
  /* `plain.toString()` zero-pads the year to 4 digits, which `<input
     type="date">` requires. Building the string by hand with
     `${plain.year}` instead would emit `"2-02-02"` for year 0002 — a
     value the input can't parse, blanking the day/month segments
     while the user is still mid-typing the year. */
  return plain.toString({ calendarName: 'never' })
}

function offsetIsoDate(date, days) {
  if (days == null) return null
  const plain = parsePlainDate(date) ?? Temporal.Now.plainDateISO()
  return plain.add({ days }).toString()
}

function nowIsoInstant() {
  return Temporal.Now.instant().toString()
}

function parseInstant(date) {
  if (date == null || date === '') return null
  try {
    return Temporal.Instant.from(date)
  } catch {
    try {
      return Temporal.ZonedDateTime.from(date).toInstant()
    } catch {
      return null
    }
  }
}

function isWithinDays(date, days) {
  const instant = parseInstant(date)
  if (!instant) return false
  const cutoff = Temporal.Now.instant().subtract({ hours: days * 24 })
  return Temporal.Instant.compare(instant, cutoff) >= 0
}

/** Inclusive `yyyy-MM-dd` range check using the calendar date of `date`. */
function isWithinInclusiveRange(date, from, to) {
  const plain = normalizeIsoDate(date)
  if (!plain) return false
  if (from && plain < from) return false
  if (to && plain > to) return false
  return true
}

function instantEpoch(date) {
  return parseInstant(date)?.epochMilliseconds ?? 0
}

class DateUtility {
  /**
   * Format a calendar date as day, month, and year in the given locale.
   * Returns an empty string when unset so templates can render without a guard.
   *
   * @param {string|Date|null|undefined} date
   * @param {string} [locale='fi']
   * @returns {string}
   */
  format(date, locale) {
    return formatDate(date, locale)
  }

  /**
   * Whether a calendar date is strictly before today. A date that falls on
   * today is not overdue.
   *
   * @param {string|Date|null|undefined} date
   * @returns {boolean}
   */
  overdue(date) {
    return isDateOverdue(date)
  }

  /**
   * Whole calendar days a due date is before today. Returns `0` on or
   * after today, `null` when unparseable.
   *
   * @param {string|Date|null|undefined} date
   * @returns {number|null}
   */
  daysOverdue(date) {
    return countDaysOverdue(date)
  }

  /**
   * Normalise API / form values to `yyyy-MM-dd`. Returns an empty string
   * when unset or unparseable so v-models and lexicographic comparisons
   * stay stable.
   *
   * @param {string|Date|null|undefined} date
   * @returns {string}
   */
  iso(date) {
    return normalizeIsoDate(date)
  }

  /** Alias for {@link iso} — legacy call sites use `input`. */
  input(date) {
    return normalizeIsoDate(date)
  }

  /**
   * Whether an ISO instant falls within the last `days` calendar days.
   *
   * @param {string|null|undefined} date
   * @param {number} days
   * @returns {boolean}
   */
  withinDays(date, days) {
    return isWithinDays(date, days)
  }

  /**
   * Whether `date` falls on a calendar day inside an inclusive
   * `yyyy-MM-dd` range. Omit `from` / `to` to leave that bound open.
   *
   * @param {string|Date|null|undefined} date
   * @param {string|null|undefined} from
   * @param {string|null|undefined} to
   * @returns {boolean}
   */
  withinInclusiveRange(date, from, to) {
    return isWithinInclusiveRange(date, from, to)
  }

  /**
   * Epoch milliseconds for sorting instants. Unparseable values → `0`.
   *
   * @param {string|null|undefined} date
   * @returns {number}
   */
  instantEpoch(date) {
    return instantEpoch(date)
  }

  /**
   * Add calendar days to a date and return `yyyy-MM-dd`. Unset `date`
   * defaults to today. Uses Temporal plain-date arithmetic.
   *
   * @param {string|Date|null|undefined} date
   * @param {number|null|undefined} days
   * @returns {string|null}
   */
  offset(date, days) {
    return offsetIsoDate(date, days)
  }

  /**
   * Current instant as an ISO-8601 UTC string (e.g. history row `date`).
   *
   * @returns {string}
   */
  now() {
    return nowIsoInstant()
  }
}

export default new DateUtility()
